import styled from "@emotion/styled";
import { ChangeEvent, useEffect, useState } from "react";
import { EntrySearchResults } from "../entry-search-results/EntrySearchResults";
import { Pagination } from "@kaltura/ds-react-components";
import { SearchResultsInnerComponentProps } from "@kaltura/mediaspace-shared-search";
import { BulkActionMenuItemInjectedProps, FilterValues, KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { BulkActionProps, HeaderBaseProps, SearchResultsResponse } from "./types";
import { CustomComponent } from "@kaltura/mediaspace-shared-ui";
import { KalturaESearchEntryResult } from "kaltura-typescript-client/api/types/KalturaESearchEntryResult";

export interface SearchResultsProps<T extends KalturaESearchEntryResult = KalturaESearchEntryResult>
    extends SearchResultsResponse<T>,
        SearchResultsInnerComponentProps {
    bulkButtonActions?: KmsTypeComponent<any, BulkActionProps>[];
    bulkMenuActions?: KmsTypeComponent<any, BulkActionMenuItemInjectedProps>[];
    onInvalidate?: () => void;
    additionalInvalidation?: () => void;
    filterValues?: FilterValues;
    areSearchResultsDraggable?: boolean;
    header?: KmsTypeComponent<any, HeaderBaseProps<T>>;
    mediaCard?: KmsTypeComponent;
    getDragData?: (entryId: string) => { idsToDrag: string[]; dragItemsCount: number };
}

const StyledResultsHeader = styled("div")({
    display: "flex",
    justifyContent: "space-between",
});

/**
 * Global Search Results
 */
export function SearchResults<T extends KalturaESearchEntryResult = KalturaESearchEntryResult>(
    props: SearchResultsProps<T>
) {
    const {
        entryResults,
        page = 1,
        onPageChange,
        pageSize,
        onEntryShowMore,
        isWideLayout,
        isTextSearch,
        onInvalidate,
        additionalInvalidation,
        areSearchResultsDraggable = false,
        header,
        mediaCard,
        getDragData,
    } = props;
    const [activeEntry, setActiveEntry] = useState<string>();
    const [activeItemIndex, setActiveItemIndex] = useState<number | null>(null);

    // show pager
    const pageCount = Math.ceil((entryResults?.totalCount ?? 0) / pageSize);
    const showPager = !!entryResults && entryResults.totalCount > entryResults.objects.length && pageCount > 1;

    // handle pager change
    const handleChange = (event: ChangeEvent<unknown>, value: number) => {
        onPageChange(value);
    };

    const handleMore = (searchResult: T, index: number) => {
        setActiveEntry(searchResult.object.id);
        setActiveItemIndex(index);
        onEntryShowMore(searchResult, index);
    };

    const handleInvalidate = () => {
        // reset selections
        setActiveEntry(undefined);
        setActiveItemIndex(null);

        // reset to first page
        if (page !== 1) {
            onPageChange(1);
        }

        // update container
        onInvalidate?.();
        additionalInvalidation?.();
    };

    useEffect(() => {
        // when media list updates, reset all local selections
        setActiveEntry(undefined);
        setActiveItemIndex(null);
    }, [entryResults]);

    return (
        <div className="kms-ds-my-media-results">
            {!!header && !!entryResults?.objects.length && (
                <StyledResultsHeader role="region" aria-live="polite">
                    <CustomComponent component={header} onReload={handleInvalidate} entryResults={entryResults} />
                </StyledResultsHeader>
            )}
            {!!entryResults?.objects.length && (
                <EntrySearchResults<T>
                    isTextSearch={isTextSearch}
                    isWideLayout={isWideLayout}
                    entries={entryResults.objects}
                    activeEntry={activeEntry}
                    onMore={handleMore}
                    onInvalidate={handleInvalidate}
                    activeItemIndex={activeItemIndex}
                    areSearchResultsDraggable={areSearchResultsDraggable}
                    mediaCard={mediaCard}
                    getDragData={getDragData}
                />
            )}

            {showPager && (
                <div className="kms-ds-my-media-pagination">
                    {<Pagination count={pageCount} page={page} onChange={handleChange} />}
                </div>
            )}
        </div>
    );
}

export default SearchResults;
